export const roomsTotal = (order) =>
  (order?.suits || []).reduce(
    (sum, room) => sum + (Number(room.price) || 0),
    0
  );

export const servicesTotal = (order) =>
  (order?.services || []).reduce(
    (sum, svc) => sum + (Number(svc.total_price) || 0),
    0
  );

export const tariffTotal = (order) =>
  Number(order?.price) ||
  Number(order?.tariff_price) ||
  roomsTotal(order) + servicesTotal(order);

export const finalPayPrice = (order) => Number(order?.pay_price) || 0;

export const paidSum = (order) => Number(order?.pay_sum) || 0;

export const hasRooms = (order) => Boolean(order?.suits?.length);

export const hasServices = (order) => Boolean(order?.services?.length);

const familyDiscountPercent = (order) => {
  const fromField = Number(order?.family_discount);
  if (!Number.isNaN(fromField) && fromField > 0) return fromField;

  const item = (order?.discount?.list || []).find(
    (line) => line.text === "Семейная скидка"
  );
  return item ? Number(item.name) || 0 : 0;
};

export const getPromoDiscountAmount = (order) => {
  const fromApi = Number(order?.price_promo_code);
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi;

  const promo = order?.promo_code;
  if (!promo) return 0;

  const percent = Number(promo.discount) || 0;
  if (percent <= 0) return 0;

  const tariff = tariffTotal(order);
  return Math.floor((tariff * percent) / 100 / 10) * 10;
};

export const getPromoDiscountLabel = (order) => {
  const promo = order?.promo_code;
  if (!promo) return "Промокод";
  return `Промокод «${promo.name}» (${promo.discount}%)`;
};

export const getPercentDiscountAmount = (order) => {
  const fromApi = Number(order?.price_discount);
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi;

  return getLocalPriceDiscount(order);
};

export const getLocalDiscountPercent = (order) => {
  let percent = Number(order?.additional_discount) || 0;
  percent += familyDiscountPercent(order);

  if (Number(order?.payment_option) === 1) {
    percent += 5;
  }

  return Math.min(percent, 100);
};

const getLocalPriceDiscount = (order) => {
  const tariff = tariffTotal(order);
  const percent = getLocalDiscountPercent(order);

  if (percent <= 0 || tariff <= 0) return 0;

  return Math.floor((tariff * percent) / 100 / 10) * 10;
};

const allocateDiscountParts = (parts, totalAmount) => {
  if (!parts.length) {
    return totalAmount > 0 ? [{ text: "Скидки", amount: totalAmount }] : [];
  }

  const weightSum = parts.reduce((sum, part) => sum + part.weight, 0);
  if (weightSum === 0) {
    return totalAmount > 0 ? [{ text: "Скидки", amount: totalAmount }] : [];
  }

  const lines = [];
  let allocated = 0;

  parts.forEach((part, index) => {
    const amount =
      index === parts.length - 1
        ? totalAmount - allocated
        : Math.floor(((totalAmount * part.weight) / weightSum / 10) * 10);
    allocated += amount;

    if (amount > 0) {
      lines.push({ text: part.text, amount });
    }
  });

  return lines;
};

export const getPercentDiscountLines = (order) => {
  const totalDiscount = getPercentDiscountAmount(order);
  if (totalDiscount <= 0) return [];

  const parts = [];

  if (Number(order?.payment_option) === 1) {
    parts.push({ text: "Оплата наличными (5%)", weight: 5 });
  }

  const familyPercent = familyDiscountPercent(order);
  if (familyPercent > 0) {
    parts.push({ text: "Семейная скидка", weight: familyPercent });
  }

  if (Number(order?.additional_discount) > 0) {
    parts.push({
      text: "Дополнительная скидка",
      weight: Number(order.additional_discount),
    });
  }

  return allocateDiscountParts(parts, totalDiscount);
};

export const getCalculatedPayPrice = (order) => {
  if (order?.discount?.calculated_pay != null) {
    return Number(order.discount.calculated_pay);
  }

  if (order?.calculated_pay_price != null) {
    return Number(order.calculated_pay_price);
  }

  return (
    tariffTotal(order) -
    getPromoDiscountAmount(order) -
    getPercentDiscountAmount(order)
  );
};

export const getManualAdjustment = (order) => {
  if (order?.manual_total == null) return 0;

  const fromApi = Number(order.manual_discount);
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi;

  const calculated = getCalculatedPayPrice(order);
  const payPrice = finalPayPrice(order);
  const diff = calculated - payPrice;

  return diff > 0 ? diff : 0;
};

export const getManualAdjustmentLabel = (order) =>
  order?.manual_discount_text || "Корректировка суммы";

export const hasAdjustments = (order) =>
  getPromoDiscountAmount(order) > 0 ||
  getPercentDiscountLines(order).length > 0 ||
  getManualAdjustment(order) > 0;
